import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { MailEntity } from '@source/mails/entities/mail.js';
import { AnsweringChannelType, AnsweringEntity } from '../entities/answering.js';
import { BadcontactEntity } from '../entities/badcontact.js';
import { OdooEntity } from '../entities/odoo.js';
import { getAllRelevantAndIrrelevantEmails } from './conversation.js';

/**
 * @entity BillingData
 */
@Entity()
export class BillingData extends AdornisEntity {
  static override _class = 'BillingData';

  @Field(() => String)
  usecase: Maybe<string>;

  @Field(() => String)
  month: Maybe<string>;

  @Field(() => String)
  year: Maybe<string>;

  @Field(() => Int)
  count: Maybe<number>;

  @Field(() => Int)
  messageCount: Maybe<number>;

  @Field(() => Int)
  conversationCount: Maybe<number>;

  @Field(() => Int)
  totalItems: Maybe<number>;

  @Field(() => Int)
  totalSanitized: Maybe<number>;

  @Field(() => Int)
  countWithoutRating: Maybe<number>;

  @Field(() => Int)
  countWithRating1: Maybe<number>;

  @Field(() => Int)
  countWithRating2: Maybe<number>;

  @Field(() => Int)
  countWithRating3: Maybe<number>;

  @Field(() => Int)
  countWithRating4: Maybe<number>;

  @Field(() => Int)
  countWithRating5: Maybe<number>;

  @Field(() => Int)
  countWithRating6: Maybe<number>;

  @Field(() => Int)
  countWithRating7: Maybe<number>;

  @Field(() => Int)
  countWithRating8: Maybe<number>;

  @Field(() => Int)
  countWithRating9: Maybe<number>;

  @Field(() => Int)
  countWithRating10: Maybe<number>;

  @Field(() => Int)
  countUnrateable: Maybe<number>;

  @Field(() => Int)
  noTimestampMessageCount: Maybe<number>;

  @Field(() => Float, {
    resolve: function (this: BillingData) {
      return () => {
        const totalRated =
          (this.countWithRating1 ?? 0) +
            (this.countWithRating2 ?? 0) +
            (this.countWithRating3 ?? 0) +
            (this.countWithRating4 ?? 0) +
            (this.countWithRating5 ?? 0) +
            (this.countWithRating6 ?? 0) +
            (this.countWithRating7 ?? 0) +
            (this.countWithRating8 ?? 0) +
            (this.countWithRating9 ?? 0) +
            (this.countWithRating10 ?? 0) || 1;
        return (
          ((this.countWithRating1 ?? 0) * 1 +
            (this.countWithRating2 ?? 0) * 2 +
            (this.countWithRating3 ?? 0) * 3 +
            (this.countWithRating4 ?? 0) * 4 +
            (this.countWithRating5 ?? 0) * 5 +
            (this.countWithRating6 ?? 0) * 6 +
            (this.countWithRating7 ?? 0) * 7 +
            (this.countWithRating8 ?? 0) * 8 +
            (this.countWithRating9 ?? 0) * 9 +
            (this.countWithRating10 ?? 0) * 10) /
          totalRated
        );
      };
    },
  })
  averageRating: Maybe<number>;
}

/**
 * @entity BillingSummary
 */
@Entity()
export class BillingSummary extends AdornisEntity {
  static override _class = 'BillingSummary';

  @Field(() => BillingData)
  intercom: Maybe<BillingData>;

  @Field(() => BillingData)
  odoo: Maybe<BillingData>;

  @Field(() => BillingData)
  contact: Maybe<BillingData>;

  @Field(() => BillingData)
  tag: Maybe<BillingData>;

  @Field(() => BillingData)
  priority: Maybe<BillingData>;

  @Field(() => BillingData)
  mailFiltering: Maybe<BillingData>;

  @Field(() => BillingData)
  badcontacts: Maybe<BillingData>;

  @Field(() => Int)
  numberOfMailsReceived: Maybe<number>;

  @Field(() => Int)
  numberOfMailsWithTickets: Maybe<number>;

  @Field(() => Int)
  numberOfMailsWithAnswers: Maybe<number>;

  @Field(() => Int)
  irrelevantMailCount: Maybe<number>;
}

export const getBillingSummary = registerQuery({
  operationName: 'getBillingSummary',
  type: () => BillingSummary,
  params: [
    { name: 'from', type: () => Date },
    { name: 'to', type: () => Date },
  ],
  resolve: (from: Date, to: Date) => async (gqlFields: BaseQLSelectionSet<BillingSummary>) => {
    const usecases = ['intercom', 'odoo', 'contact', 'tag', 'priority', 'mail-filtering', 'badcontacts'] as const;
    const results = new BillingSummary({});

    for (const usecase of usecases) {
      const responseBody = await getBillingSummaryForTimeFrame(usecase, from, to);
      if (responseBody) {
        results[usecase === 'mail-filtering' ? 'mailFiltering' : usecase] = new BillingData({
          _class: BillingData._class,
          month: from.getMonth() + 1,
          year: from.getFullYear(),
          ...(responseBody as object),
        });
      }
    }

    const mailCollection = await getRawCollection<EntityData<MailEntity>>(MailEntity._collectionName);
    const numberMailsReceived = await mailCollection.countDocuments({
      createdAt: {
        $gte: from,
        $lte: to,
      },
    });

    const numberOfMailsWithTickets = await mailCollection.countDocuments({
      createdAt: {
        $gte: from,
        $lte: to,
      },
      $and: [{ odooTicketID: { $exists: true } }, { odooTicketID: { $ne: null } }],
    });

    const numberOfMailsWithAnswers =
      (
        await mailCollection
          .aggregate([
            { $match: { createdAt: { $gte: from, $lte: to } } },
            { $project: { odooTicketID: 1 } },
            {
              $lookup: {
                from: AnsweringEntity._collectionName,
                let: { ticketId: '$odooTicketID' },
                // doing a lookup pipeline here for performance reasons
                pipeline: [
                  { $match: { $expr: { $eq: ['$foreignKey', '$$ticketId'] } } },
                  { $project: { _id: 1 } },
                  { $limit: 1 },
                ],
                as: 'answerHit',
              },
            },
            { $match: { 'answerHit.0': { $exists: true } } },
            { $count: 'count' },
          ])
          .toArray()
      )[0]?.count ?? 0;

    results.numberOfMailsReceived = numberMailsReceived;
    results.numberOfMailsWithTickets = numberOfMailsWithTickets;
    results.numberOfMailsWithAnswers = numberOfMailsWithAnswers;

    const irrelevantMails = (await getAllRelevantAndIrrelevantEmails()).uniqueIrrelevantEmails;

    const irrelevantMailCount = await mailCollection.countDocuments({
      createdAt: {
        $gte: from,
        $lte: to,
      },
      from: { $in: irrelevantMails.filter(Boolean).map(mail => new RegExp(mail!.replaceAll(/([^\w])/g, '\\$1'))) },
    });

    results.irrelevantMailCount = irrelevantMailCount;

    return results as unknown as BillingSummary;
  },
});

// export const getBillingSummaryForDay = registerQuery({
//   operationName: 'getBillingSummaryForDay',
//   type: () => BillingSummary,
//   params: [{ name: 'day', type: () => Date }],
//   resolve: (day: Date) => async (gqlFields: BaseQLSelectionSet<BillingSummary>) => {
//     const usecases = ['intercom', 'odoo', 'contact', 'tag', 'priority', 'mail-filtering', 'badcontacts'];
//     const results = new BillingSummary({});

//     const startOfDayParsed = startOfDay(day);
//     const endOfDayParsed = endOfDay(startOfDayParsed);

//     for (const usecase of usecases) {
//       const responseBody = await getBillingSummaryForTimeFrame(usecase, startOfDayParsed, endOfDayParsed);
//       if (responseBody) {
//         results[usecase === 'mail-filtering' ? 'mailFiltering' : usecase] = new BillingData({
//           _class: BillingData._class,
//           month: startOfDayParsed.getMonth() + 1,
//           year: startOfDayParsed.getFullYear(),
//           ...(responseBody as object),
//         });
//       }
//     }

//     const mailCollection = await getRawCollection<EntityData<MailEntity>>(MailEntity._collectionName);

//     const numberMailsReceived = await mailCollection.countDocuments({
//       createdAt: {
//         $gte: startOfDayParsed,
//         $lte: endOfDayParsed,
//       },
//     });

//     const numberOfMailsWithTickets = await mailCollection.countDocuments({
//       createdAt: {
//         $gte: startOfDayParsed,
//         $lte: endOfDayParsed,
//       },
//       $and: [{ odooTicketID: { $exists: true } }, { odooTicketID: { $ne: null } }],
//     });

//     results.numberOfMailsReceived = numberMailsReceived;
//     results.numberOfMailsWithTickets = numberOfMailsWithTickets;

//     const irrelevantMails = (await getAllRelevantAndIrrelevantEmails()).uniqueIrrelevantEmails;
//     const irrelevantMailCount = await mailCollection.countDocuments({
//       createdAt: {
//         $gte: startOfDayParsed,
//         $lte: endOfDayParsed,
//       },
//       from: { $in: irrelevantMails.filter(Boolean).map(mail => new RegExp(mail!.replaceAll(/([^\w])/g, '\\$1'))) },
//     });

//     results.irrelevantMailCount = irrelevantMailCount;

//     return results as unknown as BillingSummary;
//   },
// });

export const getBillingSummaryForTimeFrame = async (
  usecase: 'intercom' | 'odoo' | 'contact' | 'tag' | 'priority' | 'mail-filtering' | 'badcontacts',
  start: Date,
  end: Date,
) => {
  if (usecase === 'intercom') {
    return await getConversations(usecase, start, end);
  } else if (usecase === 'odoo') {
    return await getConversations(usecase, start, end);
  } else if (usecase === 'contact') {
    const collection = await getRawCollection('introspect');
    const result = await collection
      .aggregate([
        {
          $match: {
            operation: 'introspectContact',
            startedAt: {
              $gte: start,
              $lte: end,
            },
          },
        },
        { $match: { 'result.is_failed': false } },
        { $project: { _id: 1, 'args.ticket.id': 1 } },
        { $unwind: '$args' },
        { $group: { _id: '$args.ticket.id', sum: { $sum: 1 } } },
      ])
      .toArray();
    return {
      usecase,
      count: result.length,
    };
  } else if (usecase === 'tag') {
    const collection = await getRawCollection<OdooEntity>(OdooEntity._collectionName);
    const tagCount = await collection.countDocuments({
      createdAt: {
        $gte: start,
        $lte: end,
      },
      odooTags: { $exists: true, $ne: [] },
    });
    return {
      usecase,
      count: await tagCount,
    };
  } else if (usecase === 'priority') {
    return await getPriorityCount(start, end);
  } else if (usecase === 'mail-filtering') {
    return await getSpamCount(start, end);
  } else if (usecase === 'badcontacts') {
    const badContactsCollection = await getRawCollection<BadcontactEntity>(BadcontactEntity._collectionName);
    return {
      usecase,
      count: await badContactsCollection.countDocuments({
        createdAt: {
          $gte: start,
          $lte: end,
        },
        isSanitized: true,
      }),
    };
  }
};

async function getSpamCount(start: Date, end: Date) {
  const collection = await getRawCollection<MailEntity>(MailEntity._collectionName);
  const spamCount = await collection.countDocuments({
    createdAt: {
      $gte: start,
      $lte: end,
    },
    irrelevantCategory: { $exists: true, $nin: [null, undefined] },
  });

  return {
    usecase: 'mail-filtering',
    month: start.getMonth() + 1,
    year: start.getFullYear(),
    messageCount: spamCount,
  };
}

async function getPriorityCount(start: Date, end: Date) {
  const collection = await getRawCollection('introspect');
  const resultForPrioritisationOfMissingPriority = await collection
    .aggregate([
      {
        $match: {
          operation: 'introspectPriority',
          startedAt: {
            $gte: start,
            $lte: end,
          },
        },
      },
      { $match: { errors: null } },
      { $project: { _id: 1 } },
    ])
    .toArray();

  const resultForEscalationOfUnansweredTickets = await collection
    .aggregate([
      {
        $match: {
          operation: 'escalateUnansweredTickets',
          startedAt: {
            $gte: start,
            $lte: end,
          },
        },
      },
      { $match: { errors: null } },
      { $lookup: { from: 'introspect', as: 'children', localField: 'childIDs', foreignField: '_id' } },
      { $project: { _id: 1, children: 1 } },
      { $unwind: '$children' },
      { $match: { 'children.operation': 'writeToOdoo' } },
      { $project: { _id: 1 } },
    ])
    .toArray();

  logger.info(
    {
      resultForPrioritisationOfMissingPriority: resultForPrioritisationOfMissingPriority.length,
      resultForEscalationOfUnansweredTickets: resultForEscalationOfUnansweredTickets.length,
    },
    'Priority count',
  );

  return {
    usecase: 'priority',
    month: start.getMonth() + 1,
    year: start.getFullYear(),
    messageCount: resultForPrioritisationOfMissingPriority.length + resultForEscalationOfUnansweredTickets.length,
  };
}

async function getConversations(usecase: 'intercom' | 'odoo', start: Date, end: Date) {
  const type = usecase === 'intercom' ? AnsweringChannelType.INTERCOM_CONVERSATION : AnsweringChannelType.MAIL;
  const collection = await getRawCollection<AnsweringEntity>(AnsweringEntity._collectionName);
  const totalItems = await collection
    .find({
      answeringChannelType: type,
      createdAt: {
        $gte: start,
        $lte: end,
      },
    })
    .toArray();
  const messageCount = totalItems.length;
  const noTimestampMessageCount = await collection.countDocuments({
    answeringChannelType: {
      $eq: type,
    },
    createdAt: null,
  });
  const distinctCount = new Set(totalItems.map(item => item.foreignKey)).size;

  if (usecase === 'intercom') {
    // group per rating
    const missingExpectedAnswer = totalItems.filter(
      item => item.expectedAnswer === undefined || item.expectedAnswer === null,
    ).length;

    // Initialize rating arrays
    const ratingArrays: { missingExpectedAnswer: number } & Record<
      | '1rated'
      | '2rated'
      | '3rated'
      | '4rated'
      | '5rated'
      | '6rated'
      | '7rated'
      | '8rated'
      | '9rated'
      | '10rated'
      | 'unrateable',
      string[]
    > = {
      '1rated': [],
      '2rated': [],
      '3rated': [],
      '4rated': [],
      '5rated': [],
      '6rated': [],
      '7rated': [],
      '8rated': [],
      '9rated': [],
      '10rated': [],
      unrateable: [],
      missingExpectedAnswer: missingExpectedAnswer,
    };

    // Get all unique foreign keys
    const allForeignKeys = new Set<string>(totalItems.map(item => item.foreignKey).filter(Boolean) as string[]);
    // Process each foreign key
    for (const foreignKey of allForeignKeys) {
      const itemsForKey = totalItems.filter(item => item.foreignKey === foreignKey);
      const ratedItems = itemsForKey.filter(
        item => item.rating !== null && item.rating !== undefined && item.rating !== 0,
      );

      if (ratedItems.length === 0) {
        ratingArrays.unrateable.push(foreignKey);
      } else {
        const averageRating = ratedItems.reduce((sum, item) => sum + (item.rating ?? 0), 0) / ratedItems.length;
        const roundedRating = Math.floor(averageRating + 0.5); // Round to nearest integer (1.49 -> 1, 1.5 -> 2)
        const ratingKey = `${roundedRating}rated`;

        if (ratingArrays[ratingKey]) {
          ratingArrays[ratingKey].push(foreignKey);
        }
      }
    }

    return {
      usecase,
      month: start.getMonth() + 1,
      year: start.getFullYear(),
      messageCount: await messageCount,
      conversationCount: distinctCount,
      totalItems: totalItems.length,
      countWithoutRating: ratingArrays.unrateable.length,
      countWithRating1: ratingArrays['1rated'].length,
      countWithRating2: ratingArrays['2rated'].length,
      countWithRating3: ratingArrays['3rated'].length,
      countWithRating4: ratingArrays['4rated'].length,
      countWithRating5: ratingArrays['5rated'].length,
      countWithRating6: ratingArrays['6rated'].length,
      countWithRating7: ratingArrays['7rated'].length,
      countWithRating8: ratingArrays['8rated'].length,
      countWithRating9: ratingArrays['9rated'].length,
      countWithRating10: ratingArrays['10rated'].length,
      countUnrateable: ratingArrays['unrateable'].length,
      foreignKeyListWithRating: ratingArrays,
    };
  } else if (usecase === 'odoo') {
    // group per rating
    const missingExpectedAnswer = totalItems.filter(
      item => item.expectedAnswer === undefined || item.expectedAnswer === null,
    ).length;
    const countWithoutRating = totalItems.filter(item => !item.rating).length;
    const countWithRating1 = totalItems.filter(item => item.rating === 1).length;
    const countWithRating2 = totalItems.filter(item => item.rating === 2).length;
    const countWithRating3 = totalItems.filter(item => item.rating === 3).length;
    const countWithRating4 = totalItems.filter(item => item.rating === 4).length;
    const countWithRating5 = totalItems.filter(item => item.rating === 5).length;
    const countWithRating6 = totalItems.filter(item => item.rating === 6).length;
    const countWithRating7 = totalItems.filter(item => item.rating === 7).length;
    const countWithRating8 = totalItems.filter(item => item.rating === 8).length;
    const countWithRating9 = totalItems.filter(item => item.rating === 9).length;
    const countWithRating10 = totalItems.filter(item => item.rating === 10).length;
    const countWithRating0 = totalItems.filter(item => item.rating === 0).length;
    const foreignKeyListWithRating = {
      '1rated': totalItems.filter(item => item.rating === 1).map(item => item.foreignKey),
      '2rated': totalItems.filter(item => item.rating === 2).map(item => item.foreignKey),
      '3rated': totalItems.filter(item => item.rating === 3).map(item => item.foreignKey),
      '4rated': totalItems.filter(item => item.rating === 4).map(item => item.foreignKey),
      '5rated': totalItems.filter(item => item.rating === 5).map(item => item.foreignKey),
      '6rated': totalItems.filter(item => item.rating === 6).map(item => item.foreignKey),
      '7rated': totalItems.filter(item => item.rating === 7).map(item => item.foreignKey),
      '8rated': totalItems.filter(item => item.rating === 8).map(item => item.foreignKey),
      '9rated': totalItems.filter(item => item.rating === 9).map(item => item.foreignKey),
      '10rated': totalItems.filter(item => item.rating === 10).map(item => item.foreignKey),
      unrateable: totalItems.filter(item => item.rating === 0).map(item => item.foreignKey),
      missingExpectedAnswer: missingExpectedAnswer,
    };

    return {
      usecase,
      month: start.getMonth() + 1,
      year: start.getFullYear(),
      messageCount: await messageCount,
      conversationCount: distinctCount,
      totalItems: totalItems.length,
      countWithoutRating: countWithoutRating,
      countWithRating1: countWithRating1,
      countWithRating2: countWithRating2,
      countWithRating3: countWithRating3,
      countWithRating4: countWithRating4,
      countWithRating5: countWithRating5,
      countWithRating6: countWithRating6,
      countWithRating7: countWithRating7,
      countWithRating8: countWithRating8,
      countWithRating9: countWithRating9,
      countWithRating10: countWithRating10,
      countUnrateable: countWithRating0,
      foreignKeyListWithRating: foreignKeyListWithRating,
      noTimestampMessageCount: noTimestampMessageCount,
    };
  }
}
